import React, { useEffect, useState } from 'react';
import { useStyletron } from 'baseui';
import { Banner, KIND } from 'baseui/banner';
import { Button } from 'baseui/button';
import { Checkbox } from 'baseui/checkbox';
import { Menu } from 'baseui/icon';
import { Spinner } from 'baseui/spinner';
import { toaster } from 'baseui/toast';
import { StatefulTreeView } from 'baseui/tree-view';
import Router, { useRouter } from 'next/router';
import { acceptNPRequest, api, assignNPRequest, cancelNPRequest, copyNPRequest, createAssignmentDocument, declineNPRequest, deleteAssignmentDocument, deleteNPRequest, editNPRequest, sendNPRequest, setNPRequestAsDraft, submitNPRequest, unsubmitNPRequest } from '@gtintel/platform-api';
import { ScrollToTopButton } from '@gtintel/platform-atoms';
import { COOKIE_NAMES } from '@gtintel/platform-constants';
import { FeaturesContext } from '@gtintel/platform-context';
import { NPRequestStatus, ProjectStatusEnum } from '@gtintel/platform-types';
import { getSessionStorageItem, useDebounce } from '@gtintel/platform-utils';
import { DocumentsContext, DocumentsParentContext, FlatDocumentsContext } from '../components/Documents/contexts';
import LegacyChat from '../components/LegacyChat/ProjectButtonsStateContainer';
import { useFlux } from '../state/useFlux';
import { uploadFileToPresignedUrl } from '../utils';
import Attachments from './Attachments';
import ReportBuilder from './ReportBuilder';
import Requests from './Requests';
import { Services } from './Services';
import { Admin } from './admin';
import { Subjects } from './admin/Subjects';
import { Client } from './client';
import { ProjectContext, ProjectFilesContext, ProjectNavigationTreeContext, ReportContext, RequestEditorContext, RequestsContext, RequestsNavigationContext, ScopeComponentsContext, SubjectsContext } from './context';
import { SelectionState } from './context/types';
import { LegacyActivityFeed, LegacyMembers, LegacyPartner, LegacyReports } from './legacy';
import { Partner } from './partner';
import { initialState, mutations, queries } from './state';
import * as tree from './tree';
export let ProjectOverviewMode = /*#__PURE__*/function (ProjectOverviewMode) {
  ProjectOverviewMode["Admin"] = "admin-overview";
  ProjectOverviewMode["Client"] = "client-overview";
  ProjectOverviewMode["Partner"] = "partner-overview";
  return ProjectOverviewMode;
}({});
const ProjectAppContainer = ({
  projectId,
  children
}) => {
  const [state, mutate, query] = useFlux(initialState());
  const [isLoading, setIsLoading] = useState(true);
  const router = useRouter();
  const [_, section, assignmentId] = router.query.slug || [];
  const user = getSessionStorageItem(COOKIE_NAMES.INFOR_USER);
  const debouncedUpdateSubject = useDebounce(api.project.updateSubject);
  const debouncedUpdateFragment = useDebounce(api.project.updateFragment);
  const updateScope = useDebounce(api.project.updateScope);
  const updateProjectDetails = useDebounce(api.project.updateProjectV5);
  const updateProjectFlags = useDebounce(api.project.updateProjectFlagsV5);
  useEffect(() => {
    api.project.getProjectBuilderState(projectId).then(({
      data
    }) => {
      const newState = {
        project: data.project,
        scope: data.scope,
        template: data.template,
        subjectTypes: data.subjectTypes,
        jurisdictions: data.jurisdictions,
        cx_leads: data.cx_leads,
        quality_assurers: data.quality_assurers,
        currencies: data.currencies,
        discount_percentage: data.discount_percentage,
        discount_amount: data.discount_amount,
        net_price: data.net_price,
        preferred_currency: data.preferred_currency,
        np_requests: {
          ...data.np_requests,
          openId: assignmentId || null
        },
        document_tree: []
      };
      mutate(mutations.init({
        newState
      }));
      setIsLoading(false);
    });
  }, [projectId, assignmentId, mutate]);

  // -------------------------------------------------------------------------------------------------------------------
  // Project Context
  // -------------------------------------------------------------------------------------------------------------------
  const projectCtx = {
    data: () => query(queries.current.project()),
    edit: async function (changes) {
      updateProjectDetails(projectId, changes);
      mutate(mutations.update({
        changes
      }));
    },
    editFlags: function (changes) {
      updateProjectFlags(projectId, changes);
      mutate(mutations.updateFlags({
        changes
      }));
    },
    clearScopes: async function () {
      await api.project.clearScopes(projectId);
      mutate(mutations.update({
        changes: {
          scopes: [],
          instructions_html: '',
          report_template: null,
          sample_report: null
        }
      }));
      mutate(mutations.scope.clear());
    },
    createFlags: async function () {
      const changes = await api.project.createFlags(projectId);
      mutate(mutations.updateFlags({
        changes: changes.data
      }));
    },
    createSubProject: async () => {
      const created = await api.project.createSubProject(Number(projectId));
      Router.push(`/admin/project/detail/${created.data}`);
    },
    instructionsHtml: () => state.project.instructions_html,
    additionalInfo: () => state.project.additional_info,
    isEmpty: () => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(state.project.instructions_html || '', 'text/html');
      const emptyInstructionsHtml = doc.body.innerText.trim().length === 0;
      return (state.project.scopes ? state.project.scopes.length === 0 : true) && emptyInstructionsHtml;
    },
    templateOptions: () => Object.values(state.template.index).map(({
      id,
      name
    }) => ({
      id,
      label: name
    })),
    importTemplate: async templateId => {
      await api.project.importProjectFromTemplate(projectId, {
        templateId
      });

      // Refetch full project state to get all updates including auto-calculated pricing
      const {
        data: updatedData
      } = await api.project.getProjectBuilderState(projectId);
      const newState = {
        ...state,
        project: updatedData.project,
        scope: updatedData.scope
      };
      mutate(mutations.init({
        newState
      }));
    },
    acceptProject: async () => {
      await api.project.proposalAction({
        project_id: projectId,
        user_id: user['id'],
        action: 'accept'
      });
      Router.reload();
    },
    declineProject: async () => {
      await api.project.proposalAction({
        project_id: projectId,
        user_id: user['id'],
        action: 'decline'
      });
      Router.push('/partner/dashboard');
    },
    declineNetworkPartner: async () => {
      await api.project.proposalAction({
        project_id: projectId,
        user_id: user['id'],
        action: 'decline'
      });
    },
    qaCost: function () {
      const qaAssignments = Object.values(state.np_requests.index).filter(a => a.is_qa_assignment);
      if (qaAssignments.length > 0) {
        return String(qaAssignments.map(a => a.fee).reduce((partialSum, a) => partialSum + a, 0));
      }
      return state.project.qa_cost;
    },
    qaAssigned: function () {
      return state.project.qa_assigned;
    },
    qualityAssurers: function () {
      return state.quality_assurers.index;
    },
    projectId: () => projectId,
    name: () => state.project.name,
    sampleReport: () => state.project.sample_report,
    reportTemplate: () => state.project.report_template,
    createdDate: () => state.project.created_date,
    qaRequired: () => state.project.qa_required,
    requestedAnonymity: () => state.project.request_anonymity,
    requestedCompletionDate: () => state.project.requested_completion_date,
    npCompletionDate: () => state.project.np_completion_date,
    partnerPrice: () => state.project.partner_price,
    assignmentCosts: () => {
      return Object.values(state.np_requests.index).filter(a => !a.is_qa_assignment).filter(a => [NPRequestStatus.Sent, NPRequestStatus.Accepted, NPRequestStatus.Assigned, NPRequestStatus.Submitted, NPRequestStatus.Finished].includes(a.status)).map(a => a.fee).reduce((partialSum, a) => partialSum + a, 0);
    },
    clientPrice: () => state.project.final_price,
    currency: () => state.project.currency,
    currencies: function () {
      return state.currencies.reduce((memo, [key, value]) => {
        memo[key] = value;
        return memo;
      }, {});
    },
    preferredCurrency: () => state.preferred_currency,
    discountPercentage: () => state.discount_percentage,
    discountAmount: () => state.discount_amount,
    netPrice: () => state.net_price,
    cxLead: () => state.project.cx_lead,
    partner: () => state.project.partner,
    cxLeads: () => state.cx_leads.index,
    parent: () => state.project.parent || null,
    status: () => state.project.status,
    isManagedByHub: () => state.project.source === 'vnext',
    flags: () => state.project.flags,
    reports: () => state.project.reports || [],
    endDate: () => state.project.end_date,
    projectType: () => state.project.project_type ? state.project.project_type.name : '',
    scopesLength: () => state.project.scopes.length,
    subprojects: () => state.project.subprojects,
    tags: () => state.project.tags,
    npDisabled: () => !!state.project.np_disabled,
    isAssignedSubject: function (id) {
      return query(queries.np_requests.isAssignedSubject(id));
    },
    isAssignedScopeComponent: function (id) {
      return query(queries.np_requests.isAssignedScopeComponent(id));
    },
    isAssignedSubjectJurisdiction: function (subjectId, id) {
      return query(queries.np_requests.isAssignedSubjectJurisdiction(subjectId.toString(), id));
    },
    hasLeadPartnerAssignment: () => {
      return Object.values(state.np_requests.index).filter(a => a.is_lead_partner_assignment).length > 0;
    },
    hasQaAssignment: () => {
      return Object.values(state.np_requests.index).filter(a => a.is_qa_assignment).length > 0;
    },
    enableAssignments: () => state.project.enable_assignments,
    assignmentsCount: () => Object.values(state.np_requests.index).length,
    partnerAdjustmentsCost: () => state.project.partner_adjustments_cost ?? 0,
    clientAdjustmentsCost: () => state.project.client_adjustments_cost ?? 0,
    incrementAdjustmentsCost: (cost, type) => {
      mutate(mutations.incrementAdjustmentsCost(cost, type));
    }
  };

  // -------------------------------------------------------------------------------------------------------------------
  // Subject Context
  // -------------------------------------------------------------------------------------------------------------------
  const subjectsCtx = {
    list: () => query(queries.current.project()).subjects,
    edit: async function (id, changes) {
      debouncedUpdateSubject(parseInt(id), changes);
      mutate(mutations.subject.edit({
        id,
        changes
      }));
    },
    remove: async function (id) {
      api.project.deleteSubject(parseInt(id));
      mutate(mutations.subject.delete({
        id
      }));
    },
    reorder: async function (oldIndex, newIndex) {
      const subjects = [...state.project.subjects];
      const e = subjects.splice(oldIndex, 1)[0];
      subjects.splice(newIndex, 0, e);
      mutate(mutations.subject.reorder({
        subjects
      }));
      const order = subjects.map(s => s.id.toString());
      api.project.reorderProjectSubjects(projectId, {
        order
      });
    },
    addWithName: async function (name) {
      const {
        data: subject
      } = await api.project.createProjectSubject(parseInt(projectId), {
        name
      });
      mutate(mutations.subject.add(subject));
    },
    jurisdictionOptions: () => state.jurisdictions,
    jurisdictions: id => query(queries.current.jurisdictionsFor(id)),
    subjectTypes: () => {
      return state.subjectTypes.reduce((memo, [key, value]) => {
        memo[key] = value;
        return memo;
      }, {});
    },
    listAttachments: id => query(queries.current.project()).subjects.find(subject => subject.id === id).attachments,
    addAttachment: async (subjectId, file, path) => {
      const resp = await uploadFileToPresignedUrl(file, subjectId, 'subject', path).catch(e => {
        console.error(e);
        toaster.negative(`Could not upload ${file.name}`);
        return Promise.reject(e);
      });
      const result = await api.attachment.addToSubject(subjectId, {
        filename: file.name,
        path: resp.path,
        size: file.size
      });
      if (result.status === 201) {
        mutate(mutations.subject.addAttachment({
          id: subjectId,
          attachment: result.data
        }));
      }
    },
    removeAttachment: async (subjectId, attachmentId) => {
      const result = await api.attachment.delete(attachmentId);
      if (result.status === 204) {
        mutate(mutations.subject.removeAttachment({
          id: subjectId,
          attachmentId
        }));
      }
    }
  };

  // -------------------------------------------------------------------------------------------------------------------
  // Scope Context
  // -------------------------------------------------------------------------------------------------------------------
  const scopesCtx = {
    addWithName: async name => {
      const result = await api.project.createProjectScope(projectId, {
        name
      });
      mutate(mutations.scope.add({
        scope: result.data
      }));
    },
    list: () => query(queries.current.scopes()),
    edit: (scopeId, changes) => {
      mutate(mutations.scope.edit({
        changes,
        id: scopeId
      }));
      updateScope(scopeId, changes);
    },
    remove: async scopeId => {
      mutate(mutations.scope.delete({
        scopeId
      }));
      api.project.deleteScope(scopeId);
    },
    copy: async scopeId => {
      const result = await api.project.copyScope(scopeId);
      mutate(mutations.scope.add({
        scope: result.data
      }));
    },
    relatesTo() {
      return state.scope.relates_to.map(i => ({
        id: i[0],
        label: i[1]
      }));
    },
    applicability() {
      return state.scope.applicability || [];
    },
    serviceTypes() {
      return state.scope.service_types;
    },
    reorder: async (oldIndex, newIndex) => {
      const scopes = [...state.project.scopes];
      const e = scopes.splice(oldIndex, 1)[0];
      scopes.splice(newIndex, 0, e);
      mutate(mutations.scope.reorder({
        projectId: projectId,
        scopeIds: scopes
      }));
      await api.project.updateProjectComponentsOrder(projectId, scopes);
    }
  };

  // -------------------------------------------------------------------------------------------------------------------
  // Project Files Context
  // -------------------------------------------------------------------------------------------------------------------
  const projectFilesCtx = {
    addAttachment: async (file, type, path) => {
      const resp = await uploadFileToPresignedUrl(file, projectId, 'project', path).catch(e => {
        console.error(e);
        toaster.negative(`Could not upload ${file.name}`);
        return Promise.reject(e);
      });
      const data = {
        filename: file.name,
        path: resp.path,
        type: 'report_template',
        size: file.size,
        internal_path: resp.base_path
      };
      if (type === 'reportTemplate') {
        const result = await api.project.addAttachmentToProject(projectId, data);
        mutate(mutations.update({
          changes: {
            report_template: result.data.attachment
          }
        }));
      } else if (type === 'sampleReport') {
        data.type = 'sample_report';
        const result = await api.project.addAttachmentToProject(projectId, data);
        mutate(mutations.update({
          changes: {
            sample_report: result.data.attachment
          }
        }));
      } else if (type === 'document') {
        data.type = 'document';
        const result = await api.project.addDocumentToProject(projectId, data);
        mutate(mutations.addDocument({
          document: result.data
        }));
      } else {
        console.error(`Unknown attachment type: ${type}`);
      }
    },
    removeAttachment: async (documentId, type) => {
      api.attachment.delete(documentId).then(resp => {
        if (resp.status === 204) {
          if (type === 'reportTemplate') {
            mutate(mutations.update({
              changes: {
                report_template: null
              }
            }));
          } else if (type === 'sampleReport') {
            mutate(mutations.update({
              changes: {
                sample_report: null
              }
            }));
          } else if (type === 'document') {
            mutate(mutations.removeDocument({
              documentId
            }));
          }
          toaster.positive('Successfully delete a file');
        } else if (resp.status === 400) {
          toaster.negative('You do not have permission to delete this file');
        } else {
          toaster.negative('Could not delete this file');
        }
      }).catch(err => {
        console.error(err);
        toaster.negative('Could not delete this file');
      });
    },
    sampleReport: () => state.project.sample_report,
    reportTemplate: () => state.project.report_template,
    documents: () => state.project.documents,
    documentTree: () => state.document_tree,
    buildDocumentTree: documentTreeNodes => {
      mutate(mutations.documents.build({
        changes: documentTreeNodes
      }));
    }
  };

  // -------------------------------------------------------------------------------------------------------------------
  // Report Context
  // -------------------------------------------------------------------------------------------------------------------
  const reportCtx = {
    report: () => query(queries.report.tree()),
    updateFragment: async (id, body) => {
      mutate(mutations.report.updateFragment(id, body));
      debouncedUpdateFragment(id, body);
    },
    isReadOnly: () => false
  };

  // -------------------------------------------------------------------------------------------------------------------
  // Requests Context
  // -------------------------------------------------------------------------------------------------------------------
  const requestsCtx = {
    get: id => state.np_requests.index[id],
    add: async () => {
      const {
        data: np_request
      } = await api.project.createNPRequest(state.project.id);
      mutate(mutations.np_requests.add({
        request: np_request
      }));
    },
    edit: async (id, changes) => {
      const {
        data: np_request
      } = await api.project.editNPRequest(id, changes);
      mutate(mutations.np_requests.edit(id, np_request));
    },
    list: () => Object.values(state.np_requests.index),
    open: id => mutate(mutations.np_requests.open(id)),
    close: () => {
      requestNavigationCtx.navigateToIndex();
      return mutate(mutations.np_requests.close());
    },
    anyOpen: () => state.np_requests.openId,
    copy: async id => {
      const data = await copyNPRequest(id);
      mutate(mutations.np_requests.add({
        request: data
      }));
    },
    cancel: async id => {
      return cancelNPRequest(id).then(request => {
        mutate(mutations.np_requests.edit(id, {
          status: request.status
        }));
      }).catch(e => {
        console.error(e);
        toaster.negative(e.data.message);
      });
    },
    remove: async id => {
      await deleteNPRequest(id);
      mutate(mutations.np_requests.remove(id));
    },
    setLeadPartnerAssignment: async (id, value) => {
      await editNPRequest(id, {
        is_lead_partner_assignment: value
      });
      const changes = {
        partner: state.np_requests.index[id].partner
      };
      if (state.np_requests.index[id].status === NPRequestStatus.Assigned) {
        changes['status'] = String(ProjectStatusEnum.ACTIVATED);
      }
      mutate(mutations.np_requests.edit(id, {
        is_lead_partner_assignment: value
      }));
      if (value) {
        mutate(mutations.update({
          changes
        }));
      }
    }
  };

  // -------------------------------------------------------------------------------------------------------------------
  // Requests Navigation Context
  //--------------------------------------------------------------------------------------------------------------------
  const requestNavigationCtx = {
    assignmentId: () => assignmentId,
    navigateTo: assignmentId => {
      requestsCtx.open(assignmentId);
      const query = {
        slug: [projectId, section, assignmentId]
      };
      router.push({
        pathname: router.pathname,
        query
      });
    },
    navigateToIndex: () => {
      const query = {
        slug: [projectId, section]
      };
      router.push({
        pathname: router.pathname,
        query
      });
    }
  };

  // -------------------------------------------------------------------------------------------------------------------
  // Requests Editor context
  //--------------------------------------------------------------------------------------------------------------------
  const requestEditorCtx = {
    requestId: () => state.np_requests.openId || '?',
    isSent: () => query(queries.np_requests.isSent()),
    searchPartners: async (q, is_qa) => {
      let search;
      if (is_qa) {
        search = await api.project.searchQaPartners(q);
      } else {
        search = await api.project.searchPartners(q);
      }
      return search.data.matches;
    },
    setPartner: async partner => {
      await api.project.changeNPRequest(state.np_requests.openId, {
        partner: partner ? String(partner.id) : null
      });
      mutate(mutations.np_requests.setPartner(partner));
    },
    setFee: async fee => {
      await api.project.editNPRequest(state.np_requests.openId, {
        fee: fee
      });
      mutate(mutations.np_requests.setFee(fee));
    },
    setDueDate: async dueDate => {
      await api.project.editNPRequest(state.np_requests.openId, {
        due_date: dueDate
      });
      mutate(mutations.np_requests.setDueDate(dueDate));
    },
    proposedPartners: () => query(queries.np_requests.proposedPartners()),
    addProposedPartners: partners => mutate(mutations.np_requests.edit(state.np_requests.openId, {
      proposed_partners: partners
    })),
    partner: () => query(queries.np_requests.partner()),
    report: readonly => query(queries.np_requests.reportTree(readonly)),
    fullReport: () => query(queries.report.tree()),
    selectedScope: root => tree.selectedIn(root),
    selectionState: n => tree.selectionState(n),
    toggleSelected: async subtree => {
      const isSelected = tree.selectionState(subtree) === SelectionState.Empty; // because we're toggling
      const selectors = [];
      tree.visit(subtree, n => {
        if (n.data.selector.scope_component_id && n.path.startsWith(subtree.path)) {
          selectors.push(n.data.selector);
        }
      });
      api.project.changeNPRequest(state.np_requests.openId, {
        select: {
          is_selected: isSelected,
          components: selectors
        }
      });
      mutate(mutations.np_requests.changeSelection(selectors, isSelected));
    },
    send: async () => {
      return sendNPRequest(state.np_requests.openId).then(request => {
        mutate(mutations.np_requests.edit(state.np_requests.openId, {
          status: request.status
        }));
      }).catch(e => {
        console.error(e);
        toaster.negative(e);
      });
    },
    assign: async () => {
      return assignNPRequest(state.np_requests.openId).then(request => {
        mutate(mutations.np_requests.edit(state.np_requests.openId, {
          status: NPRequestStatus.Assigned,
          assigned_date: request.assigned_date
        }));
      }).catch(e => {
        console.error(e);
        toaster.negative(e);
      });
    },
    cancel: async () => {
      return cancelNPRequest(state.np_requests.openId).then(request => {
        mutate(mutations.np_requests.edit(state.np_requests.openId, {
          status: request.status
        }));
      }).catch(e => {
        console.error(e);
        toaster.negative(e);
      });
    },
    submit: async () => {
      return submitNPRequest(state.np_requests.openId).then(request => {
        mutate(mutations.np_requests.edit(state.np_requests.openId, {
          status: request.status
        }));
      }).catch(e => {
        console.error(e);
        toaster.negative(e);
      });
    },
    unsubmit: async () => {
      return unsubmitNPRequest(state.np_requests.openId).then(request => {
        mutate(mutations.np_requests.edit(state.np_requests.openId, {
          status: request.status
        }));
      }).catch(e => {
        console.error(e);
        toaster.negative(e);
      });
    },
    decline: async () => {
      return declineNPRequest(state.np_requests.openId).then(request => {
        mutate(mutations.np_requests.edit(state.np_requests.openId, {
          status: request.status
        }));
      }).catch(e => {
        console.error(e);
        toaster.negative(e);
      });
    },
    accept: async () => {
      return acceptNPRequest(state.np_requests.openId).then(request => {
        mutate(mutations.np_requests.edit(state.np_requests.openId, {
          status: request.status
        }));
      }).catch(e => {
        console.error(e);
        toaster.negative(e);
      });
    },
    setDraft: async () => {
      return setNPRequestAsDraft(state.np_requests.openId).then(request => {
        mutate(mutations.np_requests.edit(state.np_requests.openId, {
          status: request.status
        }));
      }).catch(e => {
        console.error(e);
        toaster.negative(e);
      });
    },
    isScopeSelected: () => !query(queries.np_requests.isEmptySelection()),
    status: () => query(queries.np_requests.status()),
    fee: () => query(queries.np_requests.fee()),
    dueDate: () => query(queries.np_requests.dueDate()),
    currency: () => query(queries.np_requests.currency()),
    acceptedDate: () => query(queries.np_requests.acceptedDate()),
    activatedDate: () => query(queries.np_requests.activatedDate()),
    submittedDate: () => query(queries.np_requests.submittedDate()),
    finishedDate: () => query(queries.np_requests.finishedDate()),
    declinedDate: () => query(queries.np_requests.declinedDate()),
    cancelledDate: () => query(queries.np_requests.cancelledDate()),
    investigatorNotes: () => state.np_requests.index[state.np_requests.openId]?.investigator_notes ?? '',
    toggleFinished: async () => {
      const {
        data: model
      } = await api.project.finishedNPRequest(state.np_requests.openId);
      mutate(mutations.np_requests.edit(model.id, model));
    },
    name: () => state.np_requests.index[state.np_requests.openId].name,
    setName: async name => {
      await editNPRequest(state.np_requests.openId, {
        name
      });
      mutate(mutations.np_requests.edit(state.np_requests.openId, {
        name
      }));
    },
    isQaAssignment: () => state.np_requests.index[state.np_requests.openId].is_qa_assignment,
    setIsQaAssignment: async value => {
      await editNPRequest(state.np_requests.openId, {
        is_qa_assignment: value
      });
      mutate(mutations.np_requests.edit(state.np_requests.openId, {
        is_qa_assignment: value
      }));
    },
    isLeadPartnerAssignment: () => state.np_requests.index[state.np_requests.openId].is_lead_partner_assignment,
    id: () => state.np_requests.openId
  };

  // -------------------------------------------------------------------------------------------------------------------
  // Requests Documents Context
  //--------------------------------------------------------------------------------------------------------------------
  const requestsDocumentsCtx = {
    documents: () => {
      return state.np_requests.index[state.np_requests.openId].documents;
    },
    addAttachment: async (file, type, path) => {
      const resp = await uploadFileToPresignedUrl(file, state.np_requests.openId, 'assignment', path).catch(e => {
        console.error(e);
        toaster.negative(`Could not upload ${file.name}`);
        return Promise.reject(e);
      });
      const data = {
        filename: file.name,
        path: resp.path,
        type: 'document',
        size: file.size,
        internal_path: resp.base_path
      };
      const doc = await createAssignmentDocument(state.np_requests.openId, data);
      mutate(mutations.np_requests.edit(state.np_requests.openId, {
        documents: [...state.np_requests.index[state.np_requests.openId].documents, doc]
      }));
    },
    removeAttachment: async (documentId, type) => {
      await deleteAssignmentDocument(state.np_requests.openId, documentId);
      mutate(mutations.np_requests.edit(state.np_requests.openId, {
        documents: [...state.np_requests.index[state.np_requests.openId].documents.filter(d => d.id !== documentId)]
      }));
    }
  };
  if (isLoading) return <Spinner />;
  return <ProjectContext.Provider value={projectCtx}>
      <ProjectFilesContext.Provider value={projectFilesCtx}>
        <DocumentsParentContext.Provider value={projectCtx}>
          <DocumentsContext.Provider value={projectFilesCtx}>
            <ScopeComponentsContext.Provider value={scopesCtx}>
              <SubjectsContext.Provider value={subjectsCtx}>
                <ReportContext.Provider value={reportCtx}>
                  <RequestsNavigationContext.Provider value={requestNavigationCtx}>
                    <RequestsContext.Provider value={requestsCtx}>
                      <RequestEditorContext.Provider value={requestEditorCtx}>
                        <FlatDocumentsContext.Provider value={requestsDocumentsCtx}>
                          {children}
                          <ScrollToTopButton />
                        </FlatDocumentsContext.Provider>
                      </RequestEditorContext.Provider>
                    </RequestsContext.Provider>
                  </RequestsNavigationContext.Provider>
                </ReportContext.Provider>
              </SubjectsContext.Provider>
            </ScopeComponentsContext.Provider>
          </DocumentsContext.Provider>
        </DocumentsParentContext.Provider>
      </ProjectFilesContext.Provider>
    </ProjectContext.Provider>;
};
function ProjectApp({
  mode
}) {
  switch (mode) {
    case ProjectOverviewMode.Admin:
      return <Admin />;
    case ProjectOverviewMode.Partner:
      return <Partner />;
    case ProjectOverviewMode.Client:
      return <Client />;
    default:
      return null;
  }
}
const ProjectNavigator = () => {
  const router = useRouter();
  const [projectId, urlSection] = router.query.slug;
  const onNavigate = newSection => {
    router.push({
      pathname: router.pathname,
      query: {
        slug: [projectId, newSection]
      }
    });
  };
  return <ProjectAppContainer projectId={projectId}>
      <ProjectLayout section={urlSection} onNavigate={onNavigate} />
    </ProjectAppContainer>;
};
const Destination = ({
  id,
  label
}) => {
  const [css, theme] = useStyletron();
  const ctx = React.useContext(ProjectNavigationTreeContext);
  const base = {
    cursor: 'pointer',
    color: theme.colors.gray500,
    backgroundColor: 'transparent',
    border: 'none',
    padding: '0'
  };
  const active = {
    ...base,
    color: theme.colors.primary,
    fontWeight: '700'
  };
  return <button className={css(ctx.isActive(id) ? active : base)} onClick={() => ctx.navigate(id)} data-testid={`nav-${id.toLocaleLowerCase()}`}>
      {label}
    </button>;
};
const ProjectLayout = ({
  onNavigate,
  section
}) => {
  const [css, theme] = useStyletron();
  const projectCtx = React.useContext(ProjectContext);
  const requestsCtx = React.useContext(RequestsContext);
  const subjectsCtx = React.useContext(SubjectsContext);
  const [isFolded, setIsFolded] = React.useState(true);
  const ctx = {
    navigate: id => {
      setIsFolded(true);
      onNavigate(id);
    },
    isActive: id => id === section,
    data: () => {
      const routes = [{
        id: 'overview',
        label: () => <Destination id="overview" label={`Project #${projectCtx.projectId()}`} />
      }, {
        id: 'documents',
        label: () => <Destination id="documents" label="Documents" />
      }, {
        id: 'services',
        label: () => <Destination id="services" label="Services" />
      }, {
        id: 'subjects',
        label: () => <Destination id="subjects" label="Subjects" />
      }, {
        id: 'legacy-partners',
        label: () => <Destination id="legacy-partners" label="Legacy Partners" />
      }, {
        id: 'assignments',
        label: () => <Destination id="assignments" label="Assignments" />
      }, {
        id: 'report',
        label: () => <Destination id="report" label="Reports" />
      },
      /*{
        id: 'report-builder',
        label: () => <Destination id="report-builder" label="Report" />,
      },*/
      {
        id: 'members',
        label: () => <Destination id="members" label="Members" />
      }, {
        id: 'activity',
        label: () => <Destination id="activity" label="Activity" />
      }];
      return routes;
    }
  };
  if (!ctx.data().map(e => e.id).includes(section)) {
    onNavigate(ctx.data()[0].id.toString());
  }
  const tree = <StatefulTreeView data={ctx.data()} indentGuides />;
  let view = null;
  switch (section) {
    case 'overview':
      view = <ProjectApp mode={ProjectOverviewMode.Admin} />;
      break;
    case 'subjects':
      view = <Subjects />;
      break;
    case 'services':
      view = <Services />;
      break;
    case 'documents':
      view = <Attachments canViewAllAttachments isUserNP={false} />;
      break;
    case 'report':
      view = <LegacyReports id={projectCtx.projectId()} />;
      break;
    case 'report-builder':
      view = <ReportBuilder />;
      break;
    case 'activity':
      view = <LegacyActivityFeed id={projectCtx.projectId()} />;
      break;
    case 'members':
      view = <LegacyMembers id={projectCtx.projectId()} />;
      break;
    case 'assignments':
      view = <Requests />;
      break;
    case 'legacy-partners':
      view = <>
          <Banner title="Project Partner" kind={projectCtx.npDisabled() ? KIND.positive : KIND.info}>
            <p>
              Running this project in Assignments Only mode means that no
              Project Partner is required.
              <br />
              Clients will not see or be able to chat with a Network Partner.
            </p>
            <Checkbox checkmarkType="toggle" checked={projectCtx.npDisabled()} onChange={e => projectCtx.edit({
            np_disabled: e.target.checked
          })}>
              Disable Project Partner
            </Checkbox>
          </Banner>

          <LegacyPartner id={projectCtx.projectId()} />
          <ProjectApp mode={ProjectOverviewMode.Partner} />
        </>;
      break;
  }
  const container = css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start'
  });
  const nav = css({
    margin: '0 2em 0 0',
    width: '185px',
    flexGrow: '0',
    flexShrink: '0',
    '@media (max-width: 600px)': {
      display: 'none'
    },
    position: 'sticky',
    top: '88px'
  });
  const foldSnap = css({
    display: 'none',
    '@media (max-width: 600px)': {
      display: 'block',
      position: 'sticky',
      top: '64px',
      zIndex: 11
    }
  });
  const main = css({
    margin: '0 1em',
    minWidth: '360px',
    maxWidth: '1000px',
    flexGrow: '1'
  });
  useEffect(() => {
    const onResizeWindow = () => {
      if (window.outerWidth <= 600) {
        setIsFolded(true);
      }
    };
    window.addEventListener('resize', onResizeWindow);
    return () => window.removeEventListener('resize', onResizeWindow);
  }, []);
  const navTriggerOverrides = {
    Root: {
      style: {
        width: '100%',
        marginBottom: '20px'
      }
    }
  };
  const navTriggerColors = isFolded ? {
    backgroundColor: theme.colors.backgroundSecondary,
    color: theme.colors.secondary
  } : {
    backgroundColor: theme.colors.gray200,
    color: theme.colors.secondary
  };
  const navTrigger = <Button overrides={navTriggerOverrides} colors={navTriggerColors} startEnhancer={Menu} onClick={() => setIsFolded(f => !f)}>
      {projectCtx.name()}
    </Button>;
  const featuresCtx = {
    ...React.useContext(FeaturesContext)
  };
  const chatRerenderDependency = 'rerender/'.concat(requestsCtx.list().map(r => `${r.id}-${r.status}`).sort().join(':'));
  return <FeaturesContext.Provider value={featuresCtx}>
      <ProjectNavigationTreeContext.Provider value={ctx}>
        <LegacyChat rerenderDependency={chatRerenderDependency} projectId={projectCtx.projectId()} />
        <div className={foldSnap}>{navTrigger}</div>
        <div className={container}>
          <div className={nav}>{tree}</div>
          <div className={main}>{isFolded ? view : tree}</div>
        </div>
      </ProjectNavigationTreeContext.Provider>
    </FeaturesContext.Provider>;
};
export { ProjectNavigator, ProjectAppContainer, ProjectApp };